import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { LRS_ACTIONS } from '@techchange/components';

import { RESOURCE_TYPES } from '../../../../constants';
import RuleEditorForm from '../../RuleEditorForm';
import getObjectOptionsMap from '../../utils/getObjectOptionsMap';
import getObjectTypeMap from '../../utils/getObjectTypeMap';

const propTypes = {

	/**
	 * The resource.
	 */
	resource: PropTypes.shape({

		/**
		 * The unique ID of the resource.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The resource type.
		 */
		type: PropTypes.oneOf([RESOURCE_TYPES.SLIDE]).isRequired,

		/*
		 * The resource title.
		 */
		title: PropTypes.string.isRequired,

		/**
		 * The list of children resources (if any) for the slide resource.
		 */
		children: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The resource ID.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * The resource type.
			 */
			type: PropTypes.oneOf([RESOURCE_TYPES.LINK]).isRequired,

			/**
			 * The resource title.
			 */
			title: PropTypes.string.isRequired,

		})),

	}).isRequired,

	/**
	 * The rule for the resource.
	 */
	rule: PropTypes.shape({

		/**
		 * The condition for the rule.
		 */
		condition: PropTypes.shape({

			/**
			 * The list of subconditions for the condition.
			 */
			subconditions: PropTypes.arrayOf(PropTypes.shape({

				/**
				 * The subcondition ID.
				 */
				id: PropTypes.string,

			})),

		}),

	}),

	/**
	 * The parent resource.
	 */
	parent: PropTypes.shape({

		/**
		 * The unique ID of the parent resource.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The parent resource type.
		 */
		type: PropTypes.oneOf([
			RESOURCE_TYPES.SUBMODULE_CONTENT,
			RESOURCE_TYPES.SUBMODULE,
		]).isRequired,

		/*
		 * The parent resource title.
		 */
		title: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * The current user's ID.
	 */
	userId: PropTypes.string.isRequired,

	/**
	 * The list of gravity forms.
	 */
	gravityForms: PropTypes.arrayOf(PropTypes.shape({

		title: PropTypes.string.isRequired,

		tc_uuid: PropTypes.string.isRequired,

	})),

	/**
	 * Callback function to handle cancelling the editor for the completion rule.
	 */
	onCancel: PropTypes.func.isRequired,

	/**
	 * Callback function to handle saving the editor for the completion rule.
	 */
	onSave: PropTypes.func.isRequired,

};

const RuleEditor = (props) => {

	const { resource, gravityForms } = props;

	const intl = useIntl();

	// Defines the list of possible completion requirements,
	// i.e. subconditions, for the completion rule.
	const initialValues = [{
		active: false,
		action: LRS_ACTIONS.VIEWED, // Choose a default action
		object_type: RESOURCE_TYPES.SLIDE, // First, a subcondition related to the resource itself
		object_id: resource.id,
		object_title: resource.title,
		parent_object_type: null,
		parent_object_id: null,
		parent_object_title: null,
	}];

	const children = resource.children || [];

	// Loop through the child resource links (if any) for the slide resource and add
	// each one as a possible completion requirement
	for (let i = 0, len = children.length; i < len; i++) {

		const child = children[i];

		// TODO: show warning for links that are not synced across translations
		// and disable active checkbox. For now we'll just hide links that are not synced
		if (child.type === RESOURCE_TYPES.LINK && child.active && child.data?.link_sync) {

			initialValues.push({
				active: false,
				action: LRS_ACTIONS.CLICKED, // Choose a default action
				object_type: RESOURCE_TYPES.LINK, // A subcondition related to the link
				object_id: child.id,
				object_title: child.title,
				parent_object_type: null,
				parent_object_id: null,
				parent_object_title: null,
			});

		}

	}

	let allowCustomRequirements = false;

	let objectTypeOptionsMap = {};

	// Allow support for choosing Gravity Form submissions...

	const objectOptionsMap = getObjectOptionsMap(intl, {
		gravityForms,
	});

	// Only allow object types that we have object options for...
	const objectTypes = Object.keys(objectOptionsMap);

	// Check to make sure that at least one valid gravity form exists...
	if (objectTypes.length > 0) {

		allowCustomRequirements = true;
		objectTypeOptionsMap = getObjectTypeMap(intl, objectTypes);

	}

	return (
		<RuleEditorForm
			initialValues={initialValues}
			objectTypeOptionsMap={objectTypeOptionsMap}
			objectOptionsMap={objectOptionsMap}
			allowCustomRequirements={allowCustomRequirements}
			{...props} />
	);

};

RuleEditor.propTypes = propTypes;

export default RuleEditor;
